export type MarketCode = "Global" | "US" | "CA" | "UK" | "EU" | "DE" | "FR" | "ES" | "ZM";

export type AlternateLink = {
  hrefLang: string;
  href: string;
};

export type MarketProfile = {
  code: MarketCode;
  routeKey: string;
  routePrefix: string;
  navLabel: string;
  countryName: string;
  marketLabel: string;
  locale: string;
  hrefLang?: string;
  currencyCode: string;
  headline: string;
  guideHeadline: string;
  positioning: string;
  guideDescription: string;
  badge: string;
  primaryToolSlugs: string[];
  guideKeywords: string[];
  priorityTopics: string[];
  sourceAuthorities: string[];
  scenarioQuestions: Array<{
    title: string;
    body: string;
  }>;
};

export const MARKET_PROFILES: Record<MarketCode, MarketProfile> = {
  Global: {
    code: "Global",
    routeKey: "tools",
    routePrefix: "/tools",
    navLabel: "Global",
    countryName: "Global",
    marketLabel: "Global reference",
    locale: "en",
    hrefLang: "x-default",
    currencyCode: "USD",
    headline: "Financial Calculators for Real-Life Money Decisions",
    guideHeadline: "Financial Guides, Calculators & Money Scenarios",
    positioning:
      "FinancialMetrics.report turns real-life financial questions into clear calculations, scenarios, and reports, with North America and Europe as the main growth markets.",
    guideDescription:
      "Read practical financial guides linked to calculators for taxes, loans, savings, investing, payroll, budgeting, and real-life money scenarios.",
    badge: "Universal tools",
    primaryToolSlugs: [],
    guideKeywords: [],
    priorityTopics: [
      "Personal tax, salary, VAT, and sales tax explainers",
      "Mortgage, debt, and affordability calculators",
      "Savings, investing, retirement, and compounding guides",
    ],
    sourceAuthorities: [
      "Official tax agencies",
      "Consumer finance regulators",
      "Central bank and inflation data sources",
    ],
    scenarioQuestions: [
      {
        title: "Which calculator should I start with?",
        body: "Start from the decision you need to make: tax, salary, debt, mortgage, savings, investing, or business planning.",
      },
      {
        title: "How should I verify a result?",
        body: "Use the methodology notes and official-source references before relying on regulated tax, payroll, lending, or investment figures.",
      },
    ],
  },
  US: {
    code: "US",
    routeKey: "us",
    routePrefix: "/us",
    navLabel: "United States",
    countryName: "United States",
    marketLabel: "North America",
    locale: "en-US",
    hrefLang: "en-us",
    currencyCode: "USD",
    headline: "US Financial Calculators",
    guideHeadline: "United States Financial Guides",
    positioning:
      "Estimate federal tax, paycheck pressure, 401(k) growth, Social Security planning, mortgage costs, credit card payoff, and debt scenarios for US financial decisions.",
    guideDescription:
      "Read US financial guides linked to calculators for taxes, retirement accounts, mortgage decisions, credit cards, student loans, savings, and investing.",
    badge: "US market focus",
    primaryToolSlugs: [
      "income-tax-calculator",
      "us-401k-calculator",
      "social-security-calculator",
      "sales-tax-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "credit-cards-payoff-calculator",
      "student-loan-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "debt-to-income",
      "debt-payoff-calculator",
      "retirement-calculator",
      "compound-interest",
      "investment-return-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "savings-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "cap-rate-calculator",
      "cash-on-cash-return-calculator",
      "gross-rent-multiplier-calculator",
    ],
    guideKeywords: ["us", "united states", "irs", "401", "social security", "student loan", "mortgage", "credit", "sales tax", "federal"],
    priorityTopics: [
      "Federal income tax, withholding, FICA, and state tax explainers",
      "401(k), IRA, Social Security, and retirement contribution planning",
      "Mortgage affordability, credit card payoff, student loan, and debt-to-income content",
    ],
    sourceAuthorities: ["Internal Revenue Service", "Consumer Financial Protection Bureau", "Investor.gov", "Social Security Administration"],
    scenarioQuestions: [
      {
        title: "How much of my salary is actually spendable?",
        body: "Model taxes, withholding, debt payments, retirement savings, and recurring bills before committing to a mortgage or major purchase.",
      },
      {
        title: "Should extra cash go to debt or retirement?",
        body: "Compare interest cost, employer match, tax treatment, and emergency liquidity before choosing one path.",
      },
    ],
  },
  CA: {
    code: "CA",
    routeKey: "canada",
    routePrefix: "/canada",
    navLabel: "Canada",
    countryName: "Canada",
    marketLabel: "North America",
    locale: "en-CA",
    hrefLang: "en-ca",
    currencyCode: "CAD",
    headline: "Canada Financial Calculators",
    guideHeadline: "Canada Financial Guides",
    positioning:
      "Plan Canadian household and business scenarios around income, mortgages, GST/HST-style sales tax questions, savings goals, debt payoff, and retirement contributions.",
    guideDescription:
      "Read Canada-focused financial guides for mortgages, debt payoff, savings, investing, GST/HST context, RRSP/TFSA planning, and practical household budgeting.",
    badge: "Canada market focus",
    primaryToolSlugs: [
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "debt-to-income",
      "loan-repayment-calculator",
      "credit-cards-payoff-calculator",
      "debt-payoff-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "retirement-calculator",
      "investment-return-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "sales-tax-calculator",
      "budget-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "cap-rate-calculator",
      "cash-on-cash-return-calculator",
      "gross-rent-multiplier-calculator",
    ],
    guideKeywords: ["canada", "canadian", "mortgage", "gst", "hst", "rrsp", "tfsa", "savings", "debt", "credit", "retirement"],
    priorityTopics: [
      "Canadian mortgage affordability, renewal shock, debt service, and payoff planning",
      "GST/HST, sales tax, and invoice math for consumers and small businesses",
      "RRSP, TFSA, retirement savings, compounding, and inflation-adjusted planning",
    ],
    sourceAuthorities: ["Canada Revenue Agency", "Financial Consumer Agency of Canada", "Bank of Canada", "Canadian Mortgage and Housing Corporation"],
    scenarioQuestions: [
      {
        title: "Can I afford this payment after renewal?",
        body: "Stress-test mortgage payments at higher rates, then compare the result with debt-to-income and monthly budget capacity.",
      },
      {
        title: "How much should I save monthly?",
        body: "Translate a goal into monthly contributions, then test the result against compounding, inflation, and contribution consistency.",
      },
    ],
  },
  UK: {
    code: "UK",
    routeKey: "uk",
    routePrefix: "/uk",
    navLabel: "United Kingdom",
    countryName: "United Kingdom",
    marketLabel: "Europe",
    locale: "en-GB",
    hrefLang: "en-gb",
    currencyCode: "GBP",
    headline: "UK Financial Calculators",
    guideHeadline: "United Kingdom Financial Guides",
    positioning:
      "Estimate UK salary, PAYE-style take-home pay, VAT, pension contributions, mortgage payments, debt payoff, and savings scenarios with UK-focused context.",
    guideDescription:
      "Read UK financial guides linked to calculators for salary, tax, pensions, VAT, loans, mortgages, savings, and practical planning.",
    badge: "UK market focus",
    primaryToolSlugs: [
      "salary-calculator",
      "vat-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "debt-to-income",
      "loan-repayment-calculator",
      "debt-payoff-calculator",
      "credit-card-minimum-payment-calculator",
      "credit-cards-payoff-calculator",
      "student-loan-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "retirement-calculator",
      "investment-return-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "budget-calculator",
      "percentage-calculator",
      "discount-calculator",
      "margin-calculator",
      "markup-calculator",
    ],
    guideKeywords: ["uk", "united kingdom", "hmrc", "paye", "national insurance", "vat", "salary", "mortgage", "pension"],
    priorityTopics: [
      "PAYE, National Insurance, pension contributions, and net salary explainers",
      "VAT-inclusive, VAT-exclusive, reverse VAT, and invoice calculation guides",
      "Mortgage affordability, credit card minimum payments, debt payoff, savings, and pension planning content",
    ],
    sourceAuthorities: ["HM Revenue & Customs", "Financial Conduct Authority", "GOV.UK", "Office for National Statistics"],
    scenarioQuestions: [
      {
        title: "Why is my net pay lower than expected?",
        body: "Review PAYE, National Insurance, pension contributions, student loan deductions, and benefit deductions before comparing salaries.",
      },
      {
        title: "Should I include or exclude VAT?",
        body: "Separate net price, VAT amount, and gross price so business invoices and consumer prices are interpreted correctly.",
      },
    ],
  },
  EU: {
    code: "EU",
    routeKey: "europe",
    routePrefix: "/europe",
    navLabel: "Europe",
    countryName: "Europe",
    marketLabel: "European Union and wider Europe",
    locale: "en",
    hrefLang: "en",
    currencyCode: "EUR",
    headline: "Europe Financial Calculators",
    guideHeadline: "Europe Financial Guides",
    positioning:
      "Compare VAT, salary, mortgage, debt, savings, investing, and business calculations for European readers, with country-specific calculators planned for major EU markets.",
    guideDescription:
      "Read Europe-focused financial guides for VAT, salary planning, mortgages, debt, savings, investing, business margins, and cross-border financial scenarios.",
    badge: "Europe market focus",
    primaryToolSlugs: [
      "vat-calculator",
      "percentage-calculator",
      "discount-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "loan-repayment-calculator",
      "debt-to-income",
      "debt-payoff-calculator",
      "credit-cards-payoff-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "investment-return-calculator",
      "return-on-investment-calculator",
      "retirement-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "margin-calculator",
      "markup-calculator",
      "profit-margin-calculator",
      "break-even-calculator",
      "payback-period-calculator",
    ],
    guideKeywords: ["europe", "eu", "vat", "salary", "mortgage", "invoice", "investment", "savings", "business", "tax"],
    priorityTopics: [
      "EU VAT, reverse VAT, VAT-inclusive pricing, and cross-border invoice math",
      "European mortgage, debt, savings, inflation, and retirement planning explainers",
      "Country pages for Germany, France, Spain, Ireland, and the Netherlands as the content library grows",
    ],
    sourceAuthorities: ["European Commission Taxation and Customs Union", "European Central Bank", "Eurostat", "National tax authorities"],
    scenarioQuestions: [
      {
        title: "How do I compare prices with VAT included?",
        body: "Break gross prices into net amount and VAT amount before comparing invoices, quotes, or business margins.",
      },
      {
        title: "How should I compare countries?",
        body: "Use the same assumptions for salary, rent, tax, debt, and savings rates, then localize the regulated inputs country by country.",
      },
    ],
  },
  DE: {
    code: "DE",
    routeKey: "de",
    routePrefix: "/de",
    navLabel: "Germany",
    countryName: "Germany",
    marketLabel: "Europe",
    locale: "en-DE",
    hrefLang: "en-de",
    currencyCode: "EUR",
    headline: "Germany Financial Calculators",
    guideHeadline: "Germany Financial Guides",
    positioning:
      "Plan Germany-focused salary, VAT, mortgage, savings, investment, and business calculations in English while dedicated German calculators are expanded.",
    guideDescription:
      "Read Germany-focused financial guides for salary planning, VAT, mortgages, household budgeting, savings, investing, and business calculations.",
    badge: "Germany market focus",
    primaryToolSlugs: [
      "vat-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "loan-repayment-calculator",
      "debt-to-income",
      "debt-payoff-calculator",
      "credit-cards-payoff-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "investment-return-calculator",
      "retirement-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "margin-calculator",
      "break-even-calculator",
      "markup-calculator",
      "profit-margin-calculator",
      "payback-period-calculator",
    ],
    guideKeywords: ["germany", "german", "vat", "mwst", "salary", "mortgage", "savings", "investment", "business"],
    priorityTopics: [
      "Germany salary, social contribution, and net-pay calculator roadmap",
      "VAT, invoice, margin, and small-business calculation content",
      "Savings, mortgage, and long-term investing explainers for English-speaking users in Germany",
    ],
    sourceAuthorities: ["Federal Ministry of Finance", "Bundesbank", "Destatis", "European Commission"],
    scenarioQuestions: [
      {
        title: "What does a gross amount mean after VAT?",
        body: "Separate net price, VAT amount, and gross total before comparing supplier quotes or customer prices.",
      },
      {
        title: "How much housing cost is sustainable?",
        body: "Compare mortgage payments, existing debt, savings rate, and emergency reserves before choosing a purchase budget.",
      },
    ],
  },
  FR: {
    code: "FR",
    routeKey: "fr",
    routePrefix: "/fr",
    navLabel: "France",
    countryName: "France",
    marketLabel: "Europe",
    locale: "en-FR",
    hrefLang: "en-fr",
    currencyCode: "EUR",
    headline: "France Financial Calculators",
    guideHeadline: "France Financial Guides",
    positioning:
      "Plan France-focused VAT, salary, mortgage, savings, investing, and business calculations in English while dedicated French-market calculators are expanded.",
    guideDescription:
      "Read France-focused financial guides for VAT, salary planning, mortgages, savings, investing, budgeting, and business calculations.",
    badge: "France market focus",
    primaryToolSlugs: [
      "vat-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "loan-repayment-calculator",
      "debt-to-income",
      "debt-payoff-calculator",
      "credit-cards-payoff-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "investment-return-calculator",
      "retirement-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "margin-calculator",
      "percentage-calculator",
      "markup-calculator",
      "profit-margin-calculator",
      "break-even-calculator",
    ],
    guideKeywords: ["france", "french", "vat", "tva", "salary", "mortgage", "savings", "investment", "business"],
    priorityTopics: [
      "France VAT, invoice, and pricing calculations",
      "Mortgage, savings, and debt planning for English-speaking readers in France",
      "Future French translations once English-market authority pages are stronger",
    ],
    sourceAuthorities: ["impots.gouv.fr", "Banque de France", "INSEE", "European Commission"],
    scenarioQuestions: [
      {
        title: "How do I compare VAT-inclusive prices?",
        body: "Use the VAT calculator to split gross, net, and tax amounts before comparing supplier or consumer prices.",
      },
      {
        title: "What is the monthly pressure of a loan?",
        body: "Use loan, mortgage, and debt-to-income calculators together before treating a payment as affordable.",
      },
    ],
  },
  ES: {
    code: "ES",
    routeKey: "es",
    routePrefix: "/es",
    navLabel: "Spain",
    countryName: "Spain",
    marketLabel: "Europe",
    locale: "en-ES",
    hrefLang: "en-es",
    currencyCode: "EUR",
    headline: "Spain Financial Calculators",
    guideHeadline: "Spain Financial Guides",
    positioning:
      "Plan Spain-focused VAT, salary, mortgage, debt, savings, investing, and business calculations in English while dedicated Spain calculators are expanded.",
    guideDescription:
      "Read Spain-focused financial guides for VAT, salary planning, mortgages, debt payoff, savings, investing, budgeting, and business calculations.",
    badge: "Spain market focus",
    primaryToolSlugs: [
      "vat-calculator",
      "mortgage-calculator",
      "mortgage-payoff-calculator",
      "home-affordability-calculator",
      "rent-vs-buy-calculator",
      "loan-to-value-calculator",
      "loan-repayment-calculator",
      "debt-to-income",
      "debt-payoff-calculator",
      "credit-cards-payoff-calculator",
      "auto-loan-calculator",
      "car-affordability-calculator",
      "savings-calculator",
      "compound-interest",
      "investment-return-calculator",
      "retirement-calculator",
      "future-value-calculator",
      "present-value-calculator",
      "net-present-value-calculator",
      "internal-rate-of-return-calculator",
      "apr-to-apy-calculator",
      "simple-interest-calculator",
      "emergency-fund-calculator",
      "net-worth-calculator",
      "salary-to-hourly-calculator",
      "hourly-to-salary-calculator",
      "raise-calculator",
      "bonus-tax-calculator",
      "commission-calculator",
      "margin-calculator",
      "discount-calculator",
      "markup-calculator",
      "profit-margin-calculator",
      "break-even-calculator",
    ],
    guideKeywords: ["spain", "spanish", "vat", "iva", "salary", "mortgage", "savings", "investment", "business"],
    priorityTopics: [
      "Spain VAT, invoice, and price comparison calculations",
      "Mortgage, debt, savings, and investing explainers for English-speaking readers in Spain",
      "Spanish-language guide translations after the primary English market pages are established",
    ],
    sourceAuthorities: ["Agencia Tributaria", "Banco de Espana", "INE", "European Commission"],
    scenarioQuestions: [
      {
        title: "How much is VAT within the final price?",
        body: "Use net-to-gross and gross-to-net VAT calculations to understand the tax portion of a quote or invoice.",
      },
      {
        title: "How do I compare rent, mortgage, and savings?",
        body: "Model monthly housing cost, debt obligations, savings rate, and investment growth before choosing a long-term budget.",
      },
    ],
  },
  ZM: {
    code: "ZM",
    routeKey: "zm",
    routePrefix: "/zm",
    navLabel: "Archived Market",
    countryName: "Archived Market",
    marketLabel: "Archived coverage",
    locale: "en-ZM",
    hrefLang: undefined,
    currencyCode: "ZMW",
    headline: "Archived Market Calculators",
    guideHeadline: "Archived Market Guides",
    positioning:
      "Archived regional calculator coverage is retained for existing users but is not part of the current search-index strategy.",
    guideDescription:
      "Archived regional guide coverage is retained for existing users but is not part of the current search-index strategy.",
    badge: "Secondary market coverage",
    primaryToolSlugs: [],
    guideKeywords: [],
    priorityTopics: [
      "Archived payroll calculation context",
      "Archived salary deduction examples",
      "Secondary coverage maintained for users who already need these tools",
    ],
    sourceAuthorities: ["Official local tax and payroll sources"],
    scenarioQuestions: [
      {
        title: "How much pay remains after deductions?",
        body: "Model statutory deductions separately before comparing net pay with living costs.",
      },
      {
        title: "When should payroll figures be checked?",
        body: "Review calculations whenever official rates, thresholds, salary, or allowances change.",
      },
    ],
  },
};

export const PRIMARY_MARKET_CODES: MarketCode[] = ["US", "CA", "UK", "EU"];
export const EUROPE_MARKET_CODES: MarketCode[] = ["UK", "EU", "DE", "FR", "ES"];
export const LOCALIZED_ALTERNATE_MARKETS: MarketCode[] = ["EU", "US", "CA", "UK", "DE", "FR", "ES"];

export const MARKET_ROUTE_TO_CODE: Record<string, MarketCode> = Object.fromEntries(
  Object.values(MARKET_PROFILES)
    .filter((profile) => profile.code !== "Global")
    .map((profile) => [profile.routeKey, profile.code]),
) as Record<string, MarketCode>;

MARKET_ROUTE_TO_CODE.eu = "EU";

export const MARKET_COUNTRY_ID_TO_CODE: Record<string, MarketCode> = {
  global: "Global",
  us: "US",
  uk: "UK",
  ca: "CA",
  canada: "CA",
  eu: "EU",
  europe: "EU",
  de: "DE",
  fr: "FR",
  es: "ES",
  zm: "ZM",
};

export const getMarketProfile = (code?: string | null) => {
  if (!code) return MARKET_PROFILES.Global;
  const normalized = code.toUpperCase() as MarketCode;
  return MARKET_PROFILES[normalized] || MARKET_PROFILES.Global;
};

export const getMarketByRouteKey = (routeKey?: string | null) => {
  if (!routeKey) return MARKET_PROFILES.Global;
  return MARKET_PROFILES[MARKET_ROUTE_TO_CODE[routeKey.toLowerCase()]] || MARKET_PROFILES.Global;
};

export const getMarketRoutePrefix = (code?: string | null) => getMarketProfile(code).routePrefix;

export const getMarketSectionPath = (code: MarketCode, section?: "tools" | "guides") => {
  const profile = MARKET_PROFILES[code];
  if (code === "Global") return section ? `/${section}` : "/";
  return section ? `${profile.routePrefix}/${section}` : profile.routePrefix;
};

export const getMarketCodeFromCountryId = (countryId?: string | null) => {
  if (!countryId) return "Global";
  return MARKET_COUNTRY_ID_TO_CODE[countryId.toLowerCase()] || getMarketByRouteKey(countryId).code;
};

export const getGuidePathForCountryId = (slug: string, countryId?: string | null, scopeType?: string) => {
  if (scopeType === "Country-specific" && countryId) {
    const marketCode = getMarketCodeFromCountryId(countryId);
    if (marketCode !== "Global") {
      return `${MARKET_PROFILES[marketCode].routePrefix}/guides/${slug}`;
    }
  }

  return `/guides/${slug}`;
};

export const getCanonicalToolPath = (slug: string, scope?: string) => {
  const profile = getMarketProfile(scope);
  if (profile.code === "Global") return `/tools/${slug}`;
  return `${profile.routePrefix}/tools/${slug}`;
};

export const marketPathPattern = "us|uk|zm|canada|europe|eu|de|fr|es";

const normalizePath = (path = "/") => {
  const clean = path.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return clean;
};

const getSectionFromPath = (path: string): "home" | "tools" | "guides" | null => {
  const clean = normalizePath(path);
  const routeKeys = Object.keys(MARKET_ROUTE_TO_CODE).join("|");
  const marketPattern = new RegExp(`^/(${routeKeys})(/(tools|guides))?$`);

  if (clean === "/" || marketPattern.test(clean)) {
    const match = clean.match(marketPattern);
    if (!match) return "home";
    if (match[3] === "tools") return "tools";
    if (match[3] === "guides") return "guides";
    return "home";
  }

  if (clean === "/tools") return "tools";
  if (clean === "/guides") return "guides";

  return null;
};

export const getAlternateLinksForPath = (path = "/"): AlternateLink[] => {
  const clean = normalizePath(path);
  if (clean === "/zm" || clean.startsWith("/zm/")) return [];

  const section = getSectionFromPath(path);
  if (!section) return [];

  const sectionName = section === "home" ? undefined : section;
  const alternates: AlternateLink[] = [
    {
      hrefLang: "x-default",
      href: getMarketSectionPath("Global", sectionName),
    },
  ];

  for (const code of LOCALIZED_ALTERNATE_MARKETS) {
    const profile = MARKET_PROFILES[code];
    if (!profile.hrefLang) continue;
    alternates.push({
      hrefLang: profile.hrefLang,
      href: getMarketSectionPath(code, sectionName),
    });
  }

  return alternates;
};
